/**

 Important: This is loaded in the HEAD of .com so must be kept as
 minimal as possible. Carefully consider any 3rd party `require`s.

  "Server property" module for reading properties from the server set by
    an embedded script using non-standard script tags, e.g.,

        <script type="application/json" data-client-store="my-namespace">
            {
                "servPropA": "${ servPropA }",
                "servPropB": "${ servPropB }",
                "servPropC": "${ servPropC }"
            }
        </script>

    If available, you can also use Scott Flesher's <sfleshe@rei.com> JSP
    tag `js:clientStore` JSP tag, e.g.,

        <%-- Add JSP variables to the clientstore 'namespace' namespace --%>
        <js:clientStore-add namespace="my-namespace"
                values="servPropA, servPropB, servPropC" />

        <%-- Write the clientstore object to the page --%>
        <js:clientStore-write />

    @param {string} Namespace of the server properties

    @example

        var servProps = new ServerProps('my-namespace');
        var a = servProps.servPropA; // `a` contains value of `servPropA`

    @author Rob McGuire-Dale <rmcguir@rei.com>
*/
'use strict';
var retrieveData = require( './lib/retrieve-data' ).retrieveData;
var win          = require( '@rei/browser-shim' ).win;

var ServerProp = function ( namespace ) {

    if ( !namespace || typeof namespace !== 'string' ) {
        throw new TypeError( 'ServerProps must be instantiated with a namespace {String}' );
    }

    // Store namespace
    this.namespace = namespace;

    // Get the initial data and store the prop
    this._data = retrieveData( this.namespace );

    // Override initial values with values found from cookies
    this.setDataFromCookies( );

    return this;
};

/** Get a value from the server properties. If the key does not exist
    it will return `null`.

    @param {string} Key of the desired value set as a server property
        or undefined. If undefined, return all server properties as
        an object.

    @example

        // Assuming you have an instance of ServerProps called
        // `servProps`...
        servProps.get('a') // Returns the value of key 'a'
*/
ServerProp.prototype.get = function ( key ) {
    var self = this;
    var servProps = self._data;

    if ( !key ) {
        return servProps;
    }

    // TODO We should eventually just pass back undefined. But, need to see what site effect it could have.
    if ( servProps[ key ] === undefined ) {
        return null;
    }

    return servProps[ key ];

};

/**
 * This is used to override any initial configuration data with values from
 * cookies explicitly defined.
 *
 * @example
 * Cookie overrides are namespaced key/value pairs to be set as follows:
 *
 * document.cookie = client-store-[configNamespace]-[configName]=[configValue]
 *
 * document.cookie = "client-store-analytics-testAndTargetEnabled=false";
 * document.cookie = "client-store-analytics-googleTagManagerEnabled=true";
 *
 * Once a cookie configuration value is set, all of the following calls will
 * return the overridden cookie value instead of that which was initially
 * provided via retrieve-data
 */
ServerProp.prototype.setDataFromCookies = function ( ) {

    var cookie = win.document.cookie;

    var data = this._data;
    var namespace = this.namespace;

    // There is no override for this particular namespace.
    if ( cookie.indexOf( 'client-store-' + namespace ) === -1 ) {
        // Stop execution of any additional code
        return false;
    }

    // We have reached the unsual case in which someone has set a client-store
    // configuration via a cookie. Lets update the client-store value with what
    // has been given via the cookie.
    for ( var key in data ) {
        // Namespaced configuration key
        var cookieKey = 'client-store-' + namespace + '-' + key;
        // find the cookie key / value within the cookie
        var cookieMatch = cookie.match( cookieKey + '=([^;]+)' );

        // an override has been found in cookie
        if ( cookieMatch && cookieMatch[ 1 ] ) {
            // assign / override the value
            this._data[ key ] = cookieMatch[ 1 ];
        }
    }
};

ServerProp.prototype._setCookie = function ( key, value ) {
    win.document.cookie = key + '=' + value;
    this.setDataFromCookies( this.namespace );
};

module.exports = ServerProp;
